/**
 * useUpstreamManifests Hook
 *
 * Fetches operation manifests for all upstream (predecessor) nodes in the workflow.
 * This enables rich token generation based on actual operation output schemas.
 */

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useWorkflowStore } from '@/stores/workflowStore';
import type { OperationManifest, OperationInfo } from '@/types/operationManifest';
import type { SwaggerSpec } from '@/types/swagger';
import { getManifest, swaggerOperationToManifest, swaggerOperationToManifestByPath, serviceProviderOperationToManifest } from '@/services/operationManifestService';
import { isBuiltInOperation, getBuiltInManifest } from '@/data/manifests';
import { fetchServiceProviderOperationManifest } from '@/services/logicAppsApi';
import { fetchConnectorSwagger } from '@/services/operationsService';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { isManagedApiConnector } from '@/utils/connections';
import { SwaggerParser } from '@/parsers/SwaggerParser';
import {
  flattenAllActions,
  getScopeAwarePredecessors,
} from '@/utils/workflow';
import type { Action } from '@/types/workflowDefinition';
import {
  isCodingAgentAction,
  isFoundryAgentAction,
  isHostedAgentAction,
} from '@/types/workflowDefinition';

// =============================================================================
// TYPES
// =============================================================================

interface ActionDefinition {
  type?: string;
  kind?: string;
  inputs?: {
    host?: {
      // Service provider format
      apiId?: string;
      operationId?: string;
      connection?: {
        referenceName?: string;
      };
      // Managed connector (ApiConnection) format
      api?: {
        id?: string;
      };
    };
    // For ApiConnection actions - method and path for operation lookup
    method?: string;
    path?: string;
    schema?: Record<string, unknown>;
    // Service Provider actions (type: 'ServiceProvider')
    serviceProviderConfiguration?: {
      serviceProviderId?: string;
      operationId?: string;
      connectionName?: string;
    };
  };
  // Managed connector metadata
  metadata?: {
    operationMetadataId?: string;
  };
  runAfter?: Record<string, string[]>;
}

interface TriggerDefinition {
  type?: string;
  kind?: string;
  inputs?: {
    host?: {
      apiId?: string;
      operationId?: string;
      api?: {
        id?: string;
      };
      connection?: {
        referenceName?: string;
      };
    };
    schema?: Record<string, unknown>;
    // ApiConnectionNotification trigger: swagger-derived fetch/subscribe block.
    // pathTemplate.template is the runtime path (e.g. "/v3/Mail/OnNewEmail"),
    // typically without the "/{connectionId}" prefix that appears in swagger.
    fetch?: {
      method?: string;
      pathTemplate?: { template?: string };
    };
  };
  metadata?: {
    operationMetadataId?: string;
  };
}

export interface UpstreamNodeInfo {
  name: string;
  type: 'action' | 'trigger';
  operationInfo: OperationInfo | null;
  operationType: string;
  brandColor?: string;
}

export interface UpstreamManifestsResult {
  /** Map of action/trigger name to manifest */
  manifests: Record<string, OperationManifest>;
  /** Map of action/trigger name to swagger (for managed connectors) */
  swaggers: Record<string, SwaggerSpec>;
  /** List of upstream node info */
  upstreamNodes: UpstreamNodeInfo[];
  /** Whether any manifests are still loading */
  isLoading: boolean;
  /** Whether all manifest fetches have completed */
  isReady: boolean;
}

// =============================================================================
// STABLE EMPTY OBJECTS
// =============================================================================

const EMPTY_ACTIONS: Record<string, unknown> = {};
const EMPTY_TRIGGERS: Record<string, unknown> = {};

// =============================================================================
// QUERY OPTIONS
// =============================================================================

const QUERY_OPTIONS = {
  staleTime: 1000 * 60 * 60 * 8, // 8 hours
  gcTime: 1000 * 60 * 60 * 8, // 8 hours
  retry: 1,
};

/**
 * Extract the operation group name from a connector ID
 * e.g., "/serviceProviders/azureTables" -> "azuretables"
 * e.g., "connectionProviders/xmlOperations" -> "xmloperations"
 */
function extractOperationGroup(connectorId: string): string {
  const lower = connectorId.toLowerCase();

  // Handle /serviceProviders/groupName
  const serviceProviderMatch = lower.match(/\/serviceproviders\/([^/]+)/);
  if (serviceProviderMatch && serviceProviderMatch[1]) return serviceProviderMatch[1];

  // Handle connectionProviders/groupName or /connectionProviders/groupName
  const connectionProviderMatch = lower.match(/\/?connectionproviders\/([^/]+)/);
  if (connectionProviderMatch && connectionProviderMatch[1]) return connectionProviderMatch[1];

  // Handle just the name - take the last segment
  const segments = connectorId.split('/').filter(Boolean);
  return segments[segments.length - 1]?.toLowerCase() ?? connectorId.toLowerCase();
}

/**
 * Extract operation info from an action definition.
 * Handles both service provider format and managed connector (ApiConnection) format.
 *
 * @param definition - The action or trigger definition
 * @param managedApiConnections - The workflow's managed API connections (for looking up connector IDs)
 */
export function extractOperationInfo(
  definition: ActionDefinition | TriggerDefinition,
  managedApiConnections?: Record<string, { api: { id: string } }>
): OperationInfo | null {
  const host = definition.inputs?.host;

  // ApiConnectionNotification triggers expose their swagger path on
  // `inputs.fetch.pathTemplate.template`. Capture method/path early so we
  // can resolve the operation later even when host.operationId is missing
  // (older workflows, or hand-edited definitions).
  const triggerFetch = (definition as TriggerDefinition).inputs?.fetch;
  const fetchPath = triggerFetch?.pathTemplate?.template;
  const fetchMethod = triggerFetch?.method ?? (fetchPath ? 'get' : undefined);

  // Service Provider actions (type: 'ServiceProvider') store config in inputs.serviceProviderConfiguration
  const spConfig = (definition as ActionDefinition).inputs?.serviceProviderConfiguration;
  if (spConfig?.serviceProviderId && spConfig?.operationId) {
    return {
      connectorId: spConfig.serviceProviderId,
      operationId: spConfig.operationId,
    };
  }

  // Service provider format: host.apiId and host.operationId
  if (host?.apiId && host?.operationId) {
    const info: OperationInfo = {
      connectorId: host.apiId,
      operationId: host.operationId,
    };
    // Keep fetch path/method as a backup so the queryFn can recover when
    // operationId doesn't match the swagger (e.g. casing differences).
    if (fetchPath) info.path = fetchPath;
    if (fetchMethod) info.method = fetchMethod;
    return info;
  }

  // Managed connector (ApiConnection) format with explicit api.id
  const apiId = host?.api?.id;
  const operationId = (definition as ActionDefinition).metadata?.operationMetadataId;

  if (apiId && operationId) {
    return {
      connectorId: apiId,
      operationId: operationId,
    };
  }

  if (apiId) {
    const info: OperationInfo = {
      connectorId: apiId,
      operationId: operationId ?? '',
    };
    if (fetchPath) info.path = fetchPath;
    if (fetchMethod) info.method = fetchMethod;
    return info;
  }

  // Managed connector format with connection reference - look up connector ID from connections.json
  const connectionRef = host?.connection?.referenceName;
  if (connectionRef && managedApiConnections) {
    const connectionInfo = managedApiConnections[connectionRef];
    if (connectionInfo?.api?.id) {
      // Get method and path from action inputs (for path-based operation lookup)
      const actionDef = definition as ActionDefinition;
      const method = actionDef.inputs?.method ?? fetchMethod;
      const path = actionDef.inputs?.path ?? fetchPath;

      const result: OperationInfo = {
        connectorId: connectionInfo.api.id,
        operationId: operationId ?? '', // operationId may not be available
      };

      // Only add method/path if they have values
      if (method) result.method = method;
      if (path) result.path = path;

      return result;
    }
  }

  return null;
}

export function getActionOperationType(action: Action): string {
  if (action.type !== 'Agent') return action.type;
  if (isHostedAgentAction(action)) return 'HostedAgent';
  if (isCodingAgentAction(action)) return 'CodingAgent';
  if (isFoundryAgentAction(action)) return 'FoundryAgent';
  return action.type;
}

/**
 * Get all predecessor action names visible to the current node.
 *
 * Delegates to {@link getScopeAwarePredecessors} so nested predecessors
 * (siblings inside a Foreach/If/Switch/Until/Scope/Agent tool) are
 * loaded in addition to top-level ones.
 */
function getPredecessorActions(
  currentActionName: string | undefined,
  actions: Record<string, unknown>,
  _triggers: Record<string, unknown>
): Set<string> {
  return getScopeAwarePredecessors(
    currentActionName,
    actions as Record<string, Action>,
  );
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to fetch manifests for all upstream nodes
 */
export function useUpstreamManifests(
  currentNodeId?: string
): UpstreamManifestsResult {
  const { getOttoToken } = useAuth();
  const { selectedApp, selectedProject } = useAppStore();

  // Get workflow data
  const actionsFromStore = useWorkflowStore((state) => state.workflow?.definition?.actions);
  const triggersFromStore = useWorkflowStore((state) => state.workflow?.definition?.triggers);
  const connectionsFromStore = useWorkflowStore((state) => state.connections);

  const actions = actionsFromStore ?? EMPTY_ACTIONS;
  const triggers = triggersFromStore ?? EMPTY_TRIGGERS;

  // Get managed API connections for looking up connector IDs
  const managedApiConnections = connectionsFromStore?.managedApiConnections;

  // Extract the action name from the node ID
  const currentActionName = useMemo(() => {
    if (!currentNodeId) return undefined;
    // Node IDs may use path form for nested actions (e.g.
    // "action-Foreach/HTTP"). Take the leaf — names are workflow-unique.
    let raw = currentNodeId;
    if (raw.startsWith('action-')) {
      raw = raw.slice('action-'.length);
      const lastSlash = raw.lastIndexOf('/');
      return lastSlash >= 0 ? raw.slice(lastSlash + 1) : raw;
    }
    if (currentNodeId.startsWith('trigger-')) {
      return currentNodeId.slice('trigger-'.length);
    }
    return currentNodeId;
  }, [currentNodeId]);

  // Get all upstream nodes (triggers + predecessor actions)
  const upstreamNodes = useMemo<UpstreamNodeInfo[]>(() => {
    const nodes: UpstreamNodeInfo[] = [];
    const predecessorNames = getPredecessorActions(currentActionName, actions, triggers);

    // Add triggers (always upstream)
    const isEditingTrigger = currentNodeId && triggers[currentNodeId] !== undefined;
    if (!isEditingTrigger) {
      for (const [name, trigger] of Object.entries(triggers)) {
        const typedTrigger = trigger as TriggerDefinition;
        nodes.push({
          name,
          type: 'trigger',
          operationInfo: extractOperationInfo(typedTrigger, managedApiConnections),
          operationType: typedTrigger.type ?? 'Unknown',
        });
      }
    }

    // Add predecessor actions. Walk the FLATTENED action tree so manifests
    // are loaded for siblings nested inside scopes (Foreach/If/Switch/etc.)
    // — without this, the token picker would list the actions but render
    // them with no schema-driven children.
    const flatActions = flattenAllActions(actions as Record<string, Action>);
    for (const [name, action] of Object.entries(flatActions)) {
      if (!predecessorNames.has(name) || name === currentActionName) continue;

      const typedAction = action as ActionDefinition;
      // Agent actions share `type: 'Agent'` across variants. Route variant
      // predecessors to their dedicated manifests so the token picker surfaces
      // variant-specific outputs such as Coding/Hosted Agent `fileOutputs`.
      const actionForGuard = action as Action;
      const operationType = getActionOperationType(actionForGuard);
      nodes.push({
        name,
        type: 'action',
        operationInfo: extractOperationInfo(typedAction, managedApiConnections),
        operationType,
      });
    }

    return nodes;
  }, [actions, triggers, currentActionName, currentNodeId, managedApiConnections]);

  // Create queries for each upstream node
  const manifestQueries = useQueries({
    queries: upstreamNodes.map((node) => ({
      // `operationType` participates in the key so that Agent variants with no
      // `operationInfo` (connectorId/operationId both undefined) still get
      // distinct cache entries — otherwise replacing a native Agent with a
      // Coding/Foundry Agent under the same name would reuse the previous
      // variant's manifest from React Query's cache.
      queryKey: [
        'upstreamManifest',
        node.name,
        node.operationInfo?.connectorId,
        node.operationInfo?.operationId,
        node.operationType,
      ],
      queryFn: async (): Promise<{ name: string; manifest: OperationManifest | null; swagger: SwaggerSpec | null }> => {
        const { operationInfo, operationType, type } = node;

        // No operation info - use built-in manifest if known, else skip runtime fetch.
        // Avoids bogus 404 calls like /operationGroups/ServiceProvider/operations/ServiceProvider
        // when the action's type isn't a real connector/operation group.
        if (!operationInfo) {
          if (isBuiltInOperation(operationType)) {
            const manifest = await getManifest({
              connectorId: operationType,
              operationId: operationType,
            });
            return { name: node.name, manifest, swagger: null };
          }
          return { name: node.name, manifest: null, swagger: null };
        }

        const { connectorId, operationId } = operationInfo;

        // 1. First check for built-in manifest (following LogicAppsUX pattern)
        if (isBuiltInOperation(operationId)) {
          const builtIn = getBuiltInManifest(operationId);
          if (builtIn) {
            return { name: node.name, manifest: builtIn, swagger: null };
          }
        }

        // 2. Check if this is a managed connector (Azure API Hub)
        const isManagedConnector = isManagedApiConnector(connectorId ?? '');
        if (isManagedConnector) {
          try {
            const token = await getOttoToken();
            if (token) {
              const swagger = await fetchConnectorSwagger(
                {
                  projectName: selectedProject ?? '',
                  accessToken: token,
                },
                connectorId
              );
              if (swagger) {
                let manifest: OperationManifest | null = null;

                // If we have operationId, use it directly
                if (operationId) {
                  manifest = swaggerOperationToManifest(swagger, operationId);
                }
                // Otherwise, try to find operation by path and method
                if (!manifest && operationInfo.path && operationInfo.method) {
                  // For ApiConnectionNotification triggers the runtime path
                  // omits the "/{connectionId}" prefix that appears in the
                  // swagger. Use the parser's resolver, which tries direct
                  // match, prefix-injection, and suffix matching, so we can
                  // recover the swagger operationId and build a manifest
                  // with the full nested response schema (e.g. the 40
                  // GraphClientReceiveMessage fields under body.value).
                  try {
                    const parser = new SwaggerParser(swagger);
                    const resolvedOpId = parser.resolveOperationIdFromFetchPath(
                      operationInfo.path,
                      operationInfo.method
                    );
                    if (resolvedOpId) {
                      manifest = swaggerOperationToManifest(swagger, resolvedOpId);
                    }
                  } catch {
                    // Parser construction or resolution failed - fall back below
                  }

                  if (!manifest) {
                    // Pass the raw runtime path directly - the regex matching in
                    // swaggerOperationToManifestByPath will handle template expressions
                    // by converting swagger paths to regex patterns where {param} becomes .*
                    manifest = swaggerOperationToManifestByPath(swagger, operationInfo.path, operationInfo.method);
                  }
                }

                return { name: node.name, manifest, swagger };
              }
            }
          } catch {
            // Failed to fetch swagger - fall through to default handling
          }
        }

        // 3. Not built-in and not managed connector - fetch from runtime API
        if (!isManagedConnector) {
          try {
              const operationGroup = extractOperationGroup(connectorId);
              const response = await fetchServiceProviderOperationManifest({
                projectName: selectedProject ?? '',
                appName: selectedApp?.name ?? '',
                getToken: getOttoToken,
                operationGroup,
                operationId,
              });
              const isTrigger = type === 'trigger' ||
                response.properties.operationType?.toLowerCase() === 'trigger';
              const manifest = serviceProviderOperationToManifest(response.properties, isTrigger);
              return { name: node.name, manifest, swagger: null };
          } catch (error) {
            console.warn(`Failed to fetch runtime manifest for ${node.name}:`, error);
          }
        }

        // 4. Fall back to default manifest
        const manifest = await getManifest(operationInfo);
        return { name: node.name, manifest, swagger: null };
      },
      enabled: !!node.name,
      ...QUERY_OPTIONS,
    })),
  });

  // Aggregate results
  const result = useMemo<UpstreamManifestsResult>(() => {
    const manifests: Record<string, OperationManifest> = {};
    const swaggers: Record<string, SwaggerSpec> = {};
    let isLoading = false;
    let isReady = true;

    for (const query of manifestQueries) {
      if (query.isLoading || query.isFetching) {
        isLoading = true;
        isReady = false;
      }
      if (!query.data) {
        isReady = false;
        continue;
      }
      const { name, manifest, swagger } = query.data;
      if (manifest) {
        manifests[name] = manifest;
      }
      if (swagger) {
        swaggers[name] = swagger;
      }
    }

    return {
      manifests,
      swaggers,
      upstreamNodes,
      isLoading,
      isReady,
    };
  }, [manifestQueries, upstreamNodes]);

  return result;
}
